import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { FormattedMessage, useIntl, defineMessages } from 'react-intl';

import HelloModal from 'common/components/hello-modal';
import HfReactHelper from 'common/utils/hf-react-helper';
import * as actions from 'hellospa/page/prep-and-send/data/actions';
import * as selectors from 'hellospa/page/prep-and-send/data/selectors';
import styles from './index.scss';

const modalPropMessages = defineMessages({
  chargeAlertTitle: {
    id: '',
    description: 'Title for one time charge modal when you have exceeded the quota of faxes you can send ',
    defaultMessage: 'This fax exceeds your quota',
  },
  chargeAlertPartialTitle: {
    id: '',
    description: 'Title for one time charge modal when you have used your free pages and will now be charged ',
    defaultMessage: 'Sending this fax will use up your free pages',
  },
  chargeAlertGraceTitle: {
    id: '',
    description: 'Title for one time charge modal when you are over your free page limit, but we give you an extra page ',
    defaultMessage: 'Have an extra page on us',
  },
  chargeAlertHighOverageTitle: {
    id: '',
    description: 'Title for one time charge modal when you are over the amount you can spend in a free trial ',
    defaultMessage: 'You\'ve hit your free trial limit',
  },
  chargeAlertFirstTimeOverageTitle: {
    id: '',
    description: 'Title for one time charge modal when you are over your free page limit for the first time ',
    defaultMessage: 'You\'ve used up all your free pages',
  },
  continue: {
    id: '',
    description: 'text for one time charge modal that lets you continue to payment screen',
    defaultMessage: 'Continue',
  },
  purchase: {
    id: '',
    description: 'text for one time charge modal that links you to the upgrade page',
    defaultMessage: 'Purchase more pages',
  },
  cancel: {
    id: '',
    description: 'text for one time charge modal that lets you close the modal and avoid payment',
    defaultMessage: 'Don\'t send',
  },
});

type Props = {
  isOpen: boolean,
  close: any,
};

const chargeAlert = 'charge_alert';
const chargeAlertPartial = 'charge_alert_partial';
const chargeAlertGrace = 'charge_alert_grace';
const chargeAlertHighOverage = 'charge_alert_high_overage';
const chargeAlertFirstTimeOverage = 'charge_alert_first_time_overage';
export default function OneTimeChargeModal({ isOpen, close }: Props) {
  const intl = useIntl();
  const dispatch = useDispatch();

  const totalPriceInCents = useSelector(selectors.totalPriceInCents);
  const displayTotalPrice = HfReactHelper.formatCurrency(intl, totalPriceInCents / 100);

  const pricePerFax = useSelector(selectors.pricePerFaxInCents);
  const displayPricePerFax = HfReactHelper.formatCurrency(intl, pricePerFax / 100);

  const initialPageLimit = useSelector(selectors.initialPageLimit);

  const overagePriceInCentsPerPage = useSelector(selectors.overagePriceInCentsPerPage);
  const displayOverflowPrice = HfReactHelper.formatCurrency(intl, overagePriceInCentsPerPage / 100);

  const internationalMultiplier = useSelector(selectors.internationalMultiplier);
  const internationalPricingUrl = HfReactHelper.urlHelper('info/internationalFaxPricing', 'hellofax');

  const chargeType = useSelector(selectors.chargeType);

  const showInternationalSection = internationalMultiplier > 1
    && [chargeAlert, chargeAlertPartial, chargeAlertGrace].includes(chargeType);
  const showFreeEmailText = [chargeAlert, chargeAlertPartial, chargeAlertGrace]
    .includes(chargeType);
  const showStandardChargeTerms = [chargeAlert, chargeAlertPartial, chargeAlertGrace]
    .includes(chargeType);
  const freeFaxPagesLeft = useSelector(selectors.freeFaxPagesLeft);
  const faxPageCurrentCount = useSelector(selectors.faxPageCurrentCount);
  const overageFaxMaxLimitInCents = useSelector(selectors.overageFaxMaxLimitInCents);
  const displayOverageFaxMaxLimit = HfReactHelper.formatCurrency(
    intl,
    overageFaxMaxLimitInCents / 100,
  );
  const pricingUrl = HfReactHelper.urlHelper('info/pricing', 'hellofax');
  let buttons = [];
  const continueButton = {
    type: 'primary',
    text: intl.formatMessage(modalPropMessages.continue),
    onClick: async () => {
      // TODO: disable/remove button on click
      dispatch(actions.sendSRAndRedirect(true));
    },
  };
  if (chargeType === chargeAlertHighOverage) {
    buttons = [
      {
        type: 'primary',
        text: intl.formatMessage(modalPropMessages.purchase),
        buttonLink: pricingUrl,
        openInNewWindow: true,
      },
    ];
  } else if ([chargeAlertPartial, chargeAlertFirstTimeOverage].includes(chargeType)) {
    buttons = [
      {
        type: 'secondary',
        text: intl.formatMessage(modalPropMessages.cancel),
        onClick: close,
      },
      continueButton,
    ];
  } else {
    buttons = [
      continueButton,
    ];
  }

  const pricingTag = (msg: string) => (
    <a
      target="_blank"
      rel="noopener noreferrer"
      href={internationalPricingUrl}
      className={styles.oneTimeChargeLink}
    >{msg}</a>
  );

  // charge_alert title
  let modalTitle = modalPropMessages.chargeAlertTitle;

  if (chargeType === chargeAlertFirstTimeOverage) {
    modalTitle = modalPropMessages.chargeAlertFirstTimeOverageTitle;
  } else if (chargeType === chargeAlertPartial) {
    modalTitle = modalPropMessages.chargeAlertPartialTitle;
  } else if (chargeType === chargeAlertGrace) {
    modalTitle = modalPropMessages.chargeAlertGraceTitle;
  } else if (chargeType === chargeAlertHighOverage) {
    modalTitle = modalPropMessages.chargeAlertHighOverageTitle;
  }

  // TODO: Make continue button link to one-time charge page
  // after Hellofax Send flow is handled + One-time-charge page is created
  return (
    <HelloModal
      width="540px"
      scrollable={false}
      isOpen={isOpen}
      onRequestClose={close}
      buttons={buttons}
      contentLabel={intl.formatMessage(modalTitle)}
    >
      <div className={styles.oneTimeChargeModalText}>
        <strong>
          {
            chargeType === chargeAlert && (
              <FormattedMessage
                id=""
                description="text for a modal on prep and send that tells you how much the total price of the fax will be"
                defaultMessage="Sending it will cost {displayTotalPrice}. "
                values={{
                  displayTotalPrice,
                }} />
            )
          }
        </strong>
        {
            chargeType === chargeAlertFirstTimeOverage && (
              <FormattedMessage
                id=""
                description="text for a modal on prep and send that tells you that you will be charged at the end of your trial"
                defaultMessage="You can keep sending faxes but you'll be charged {displayOverflowPrice} for each extra page
                at the end of your free trial. "
                values={{
                  displayOverflowPrice,
                }} />
            )
        }
        {
          chargeType === chargeAlertHighOverage && (
            <FormattedMessage
              id=""
              description="text for a modal on prep and send that tells you that you can't charge more than a certain price
              in a free trial"
              defaultMessage="You've used up your {displayOverageFaxMaxLimit} free trial overage. Upgrade your plan or purchase more pages
              then try again."
              values={{
                displayOverageFaxMaxLimit,
              }} />
          )
        }
        {
          chargeType === chargeAlertGrace && (
            <React.Fragment>
              <FormattedMessage
                id=""
                description="text for a modal on prep and send that tells you that as an exception you can send this fax for free"
                defaultMessage="You have {freeFaxPagesLeft} free pages and this fax is {faxPageCurrentCount} pages long. Because it's so close
                you can have an extra page on us. "
                values={{
                  freeFaxPagesLeft,
                  faxPageCurrentCount,
                }} />
                {
                  !showInternationalSection && (
                    <React.Fragment>
                      <br /><br />
                    </React.Fragment>
                  )
                }
            </React.Fragment>

          )
        }
        {
          chargeType === chargeAlertPartial && (
            <React.Fragment>
              <FormattedMessage
                id=""
                description="text for a modal on prep and send that tells you that you will be charged for a part of this fax"
                defaultMessage="You have {freeFaxPagesLeft} free pages and this fax is {faxPageCurrentCount} pages long.
                So, this fax will cost {displayTotalPrice} to send. "
                values={{
                  freeFaxPagesLeft,
                  faxPageCurrentCount,
                  displayTotalPrice,
                }} />
              {
                !showInternationalSection && (
                  <React.Fragment>
                    <br /><br />
                  </React.Fragment>
                )
              }
            </React.Fragment>
          )
        }
        {showInternationalSection && (
          <span>
            <FormattedMessage
              id=""
              description="text for a modal on prep and send that tells you how much the price is multiplied
              based on the country of the recipient. This text will only appear if the country has a multiplier
              of two or more."
              defaultMessage="Faxes to this country cost {internationalMultiplier} fax pages to send each fax page. For more details
              see our <a>international fax pricing</a>."
              values={{
                internationalMultiplier,
                a: pricingTag,
              }}
              tagName="span" />
            <br /><br />
          </span>
        )}

        {
          showStandardChargeTerms && (
            <span>
              <FormattedMessage
                id=""
                description="text for a modal on prep and send that tells you the details of the total fax price"
                defaultMessage="We charge {displayPricePerFax} per fax (up to {initialPageLimit} pages).
                  After {initialPageLimit} pages, it's {displayOverflowPrice} / page."
                values={{
                  displayPricePerFax,
                  initialPageLimit,
                  displayOverflowPrice,
                }} />
              <br /><br />
            </span>
          )
        }

        {
          showFreeEmailText && (
          <FormattedMessage
            id=""
            description="text for a modal on prep and send that says the submission will be free if it's going to an email"
            defaultMessage="<strong>You can send it for free as an email</strong> by closing this modal
          and entering an email address instead of a fax number."
            values={{
            // eslint-disable-next-line react/display-name
              strong: (str: string) => <strong>{str}</strong>,
            }}
            tagName="span" />
          )
        }
        <br /><br />
      </div>
    </HelloModal>
  );
}
